import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CircularProgress from "@mui/material/CircularProgress";

const styles = (theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      backgroundColor: "#1C1E20",
      maxWidth: "600px",
    },
  },
  dialogTitle: {
    backgroundColor: "rgba(232, 163, 61, 0.1)",
    borderBottom: "1px solid rgba(232, 163, 61, 0.3)",
  },
  title: {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "1.3rem",
  },
  content: {
    padding: "2rem",
  },
  impactList: {
    listStyle: "none",
    padding: 0,
    margin: "1.5rem 0",
  },
  impactItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.75rem",
    padding: "0.75rem",
    marginBottom: "0.5rem",
    backgroundColor: "rgba(255,255,255,0.03)",
    borderRadius: "8px",
  },
  impactIcon: {
    color: "#00C853",
    fontSize: "1.2rem",
    flexShrink: 0,
    marginTop: "0.1rem",
  },
  impactText: {
    color: "rgba(255,255,255,0.9)",
    fontSize: "0.95rem",
    lineHeight: 1.5,
  },
  warningBox: {
    backgroundColor: "rgba(244, 67, 54, 0.1)",
    border: "1px solid #F44336",
    borderRadius: "8px",
    padding: "1rem",
    marginTop: "1rem",
  },
  warningText: {
    color: "#F44336",
    fontSize: "0.9rem",
    fontWeight: 600,
  },
  actions: {
    padding: "1.5rem",
    gap: "1rem",
  },
  confirmButton: {
    fontWeight: 700,
  },
});

function ConfirmationDialog({ classes, open, decision, report, onConfirm, onCancel, submitting }) {
  if (!decision || !report) {
    return null;
  }

  const getDecisionConfig = () => {
    switch (decision) {
      case "VALIDATED":
        return {
          title: "Confirm: Validate Report",
          color: "#00C853",
          impacts: [
            "Apply penalty to Reported Player (based on offense severity)",
            "Boost Reporter's credibility score (+2)",
            "Update reputation history for both players",
            "Report status set to VALIDATED",
          ],
        };
      case "DISMISSED":
        return {
          title: "Confirm: Dismiss Report",
          color: "#FFC107",
          impacts: [
            "No penalty applied to Reported Player",
            "Minor penalty to Reporter's credibility (-1)",
            "Report status set to DISMISSED",
            "Reported player remains unaffected",
          ],
        };
      case "FALSE_REPORT":
        return {
          title: "Confirm: Mark as False Report",
          color: "#F44336",
          impacts: [
            "No penalty applied to Reported Player",
            "Major penalty to Reporter's credibility (-5 to -20)",
            "Compensation bonus to Reported Player (+2)",
            "Report status set to FALSE_REPORT",
          ],
        };
      default:
        return { title: "Confirm Action", color: "#E8A33D", impacts: [] };
    }
  };

  const config = getDecisionConfig();

  return (
    <Dialog open={open} onClose={onCancel} className={classes.dialog}>
      <DialogTitle className={classes.dialogTitle}>
        <Typography className={classes.title} style={{ color: config.color }}>
          {config.title}
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Typography variant="body1" style={{ color: "rgba(255,255,255,0.9)", marginBottom: "1rem" }}>
          This will affect the reputation of both the reporter and the reported player:
        </Typography>

        <ul className={classes.impactList}>
          {config.impacts.map((impact, index) => (
            <li key={index} className={classes.impactItem}>
              <CheckCircleIcon className={classes.impactIcon} style={{ color: config.color }} />
              <span className={classes.impactText}>{impact}</span>
            </li>
          ))}
        </ul>

        <Box className={classes.warningBox}>
          <Typography className={classes.warningText}>
            ⚠️ This action cannot be undone. Please review carefully before confirming.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button variant="outlined" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={submitting}
          className={classes.confirmButton}
          style={{ backgroundColor: config.color }}
          startIcon={submitting ? <CircularProgress size={20} /> : null}
        >
          {submitting ? "Processing..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ConfirmationDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  decision: PropTypes.string,
  report: PropTypes.object,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
};

ConfirmationDialog.defaultProps = {
  submitting: false,
};

export default withStyles(styles)(ConfirmationDialog);
